import React, { useMemo, useState } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { buildEquityGeometry, equityTicks } from './equityGeometry';
import { tok, LABEL, MONO, panelSx, titleSx, fmtMoney } from './ui';

// Equity on top, the underwater drawdown directly beneath on the same time axis (DWLF-306), so a
// drawdown lines up with the equity it came from. Trade entries tick the equity line.
// SVG, so colours are CSS variables via `style`.

const W = 1000;
const EQ_H = 260;
const DD_H = 90;
const GAP = 18;
const H = EQ_H + GAP + DD_H + 22;

export default function EquityDrawdownChart({ equityCurve, trades, maxDrawdownPct }) {
  const [showTrades, setShowTrades] = useState(true);
  const g = useMemo(() => buildEquityGeometry(equityCurve, { width: W, equityHeight: EQ_H, ddHeight: DD_H, gap: GAP }), [equityCurve]);
  if (!g) {
    return (
      <Box sx={panelSx}>
        <Typography component="h2" sx={titleSx}>Equity &amp; drawdown</Typography>
        <Typography sx={{ fontFamily: MONO, fontSize: '0.8125rem', color: tok('ink-55') }}>No equity curve in this result.</Typography>
      </Box>
    );
  }
  const inRange = (d) => { const t = new Date(d).getTime(); return t >= g.t0 && t <= g.t1; };
  const ticks = showTrades ? (trades || []).filter((t) => t.entryDate && inRange(t.entryDate)) : [];
  const years = [];
  for (let y = new Date(g.t0).getFullYear() + 1; y <= new Date(g.t1).getFullYear(); y += 1) years.push(y);

  return (
    <Box sx={panelSx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 1 }}>
        <Typography component="h2" sx={titleSx}>Equity &amp; drawdown</Typography>
        <ButtonBase aria-pressed={showTrades} onClick={() => setShowTrades((v) => !v)}
          sx={{ ...LABEL, fontSize: '0.625rem', height: 32, px: 1.25, gap: 0.75, border: `1px solid ${tok(showTrades ? 'ink-30' : 'ink-12')}`, color: tok(showTrades ? 'text' : 'ink-50') }}>
          <Box component="span" sx={{ width: 6, height: 6, backgroundColor: tok(showTrades ? 'accent' : 'ink-20') }} />Trades
        </ButtonBase>
      </Box>
      <svg viewBox={`0 0 ${W + 70} ${H}`} width="100%" role="img" aria-label="Equity curve with drawdown below" style={{ display: 'block' }}>
        {equityTicks(g.lo, g.hi).map((v) => (
          <g key={v}>
            <line x1={0} x2={W} y1={g.yOf(v)} y2={g.yOf(v)} style={{ stroke: tok('ink-06') }} />
            <text x={W + 8} y={g.yOf(v) + 4} fontSize="11" style={{ fill: tok('ink-50'), fontFamily: MONO }}>{fmtMoney(v)}</text>
          </g>
        ))}
        {years.map((y) => {
          const x = g.xOf(`${y}-01-01`);
          return (
            <g key={y}>
              <line x1={x} x2={x} y1={0} y2={g.ddTop + DD_H} style={{ stroke: tok('ink-05') }} />
              <text x={x + 4} y={H - 4} fontSize="11" style={{ fill: tok('ink-50'), fontFamily: MONO }}>{y}</text>
            </g>
          );
        })}
        <path d={g.equityPath} fill="none" strokeWidth={1.6} style={{ stroke: tok('accent') }} />
        {ticks.map((t, i) => {
          const x = g.xOf(t.entryDate);
          const pnl = t.netPnL ?? t.pnl ?? null;
          const tone = pnl === null ? 'ink-50' : pnl >= 0 ? 'up' : 'down'; // absent is not a win
          return <rect key={`${t.entryDate}-${i}`} x={x - 1} y={EQ_H - 10} width={2} height={10} style={{ fill: tok(tone) }} />;
        })}
        <line x1={0} x2={W} y1={g.ddTop} y2={g.ddTop} style={{ stroke: tok('ink-12') }} />
        <path d={g.ddPath} style={{ fill: tok('down-20'), stroke: tok('down') }} strokeWidth={1} />
        <text x={W + 8} y={g.ddTop + 12} fontSize="11" style={{ fill: tok('ink-50'), fontFamily: MONO }}>0%</text>
        <text x={4} y={g.ddTop + DD_H - 4} fontSize="11" style={{ fill: tok('down'), fontFamily: MONO }}>
          {maxDrawdownPct === null || maxDrawdownPct === undefined ? 'Max drawdown —' : `Max drawdown −${Math.abs(maxDrawdownPct).toFixed(2)}%`}
        </text>
      </svg>
    </Box>
  );
}
